import * as React from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import {
  IntlProvider,
  LocalizationProvider,
  loadMessages,
  load,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { createStyles, Divider, makeStyles, Theme } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
import esMessages from '../../utils/gridMessageKey.json';
import { ProgressBar } from '@progress/kendo-react-progressbars';

loadMessages(esMessages, 'fr-CA');
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones,
);
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  addNewNBCItem,
  fetchNBCItemsByID,
  getProfileNameList,
  resetFetchError,
  updateNBCItems,
  overwriteNBCItem,
  importImages,
  getAllNBCImages,
  resetNotificationMessage,
  deleteNBCItems,
  getItemsGroups,
} from '../../store/NBC/NBCAction';
import { ImageInfo, NBCItem, NBCProfile } from '../../Interfaces/NBC/NBCItem';
import {
  Checkbox,
  CheckboxChangeEvent,
  Input,
  InputChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent,
  RadioGroupChangeEvent,
} from '@progress/kendo-react-inputs';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { NBCItemCSVRecord } from '../../Interfaces/NBC/NBCItemCSVRecord';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CSVLink } from 'react-csv';
import { dateFormat, getLocalDateTime } from '../../utils/utils';
import AddNBCItemDialog from './AddNBCItemDialog';
import { LoadingPanel } from '../LoadingPanel';
import SaveIcon from '@mui/icons-material/Save';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import ApplySnapshotDialog from './ApplySnapshotDialog';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import UploadIcon from '@mui/icons-material/Upload';
import CustomCSVReader, { CsvRow, SchemaDefinition } from '../CustomCSVReader';
import { ColumnMenu, HeaderCustomCell } from '../ColumnMenus/ColumnMenu';
import ManageProfileDialog from './ManageProfileDialog';
import { saveSelectedProfile } from '../../store/UserSettings/UserSettingsActions';
import { FetchError } from '../../Interfaces/FetchError';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { RadioGroup } from '@progress/kendo-react-inputs';
import _ from 'lodash';
import ImageDialog from './ImageDialog';
import { ResultResponse } from '../../Interfaces/ResultResponse';
import DynamicDropDownList from '../DynamicDropDownList';
import { NBCActionTypes } from '../../store/NBC/NBCState';
import ManageProfileGroupsDialog from './ManageProfileGroupsDialog';
import { NBCItemGroups } from '../../Interfaces/NBC/NBCItemGroups';
import { NBC_IMAGE_IMPORT_BATCH_SIZE } from '../../utils/constants';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarButton: {
      height: 40,
      minWidth: 160,
      marginRight: 5,
    },
    toolbarButtonContainer: {
      display: 'grid',
    },
    toolbarButtonGrid: {
      display: 'flex',
      padding: '5px',
    },
    imageCell: {
      height: '48px',
      width: '75px',
      border: 'none',
    },
    column: {
      textALign: 'center',
    },
    addItemButton: {
      display: 'flex',
      marginLeft: '10px',
    },
    icon: {
      marginTop: '-5px',
      marginLeft: '5px',
    },
    send: {
      fontSize: '1.5rem',
    },
    fontSize: {
      fontSize: '0.9rem',
    },
    imageImportProgressToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
    },
  }),
);

const NBC_ITEM_CSV_SCHEMAS: Record<string, SchemaDefinition> = {
  ENHNBC: {
    required: [
      'Code',
      'Image',
      'Enabled',
      'Visible',
      'English',
      'Group',
      'Quantity Required',
      'Weight Required',
      'Special Security',
      'Hits',
      'Purchase By Department',
      'Department',
      'Price',
      'PLU',
    ],
    optional: [
      'French',
      'German',
      'Spanish',
      'Remove Toppings Only',
      'Half and Half',
      'Combo Type',
      'Purchase Option',
      'Category',
      'Customization Group',
      'Default Customization',
    ],
  },
  Enterprise: {
    required: [
      'Code',
      'ImagePath',
      'IsEnabled',
      'IsVisible',
      'English',
      'Group',
      'QuantityRequired',
      'WeightRequired',
      'SpecialSecurity',
      'Hits',
      'PurchaseByDepartment',
      'Department',
      'Price',
      'PLU',
    ],
    optional: ['French', 'German', 'Spanish'],
  },
};

const NBCItemsGrid: React.FC = ({}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();
  const tdStyle: any = { textAlign: 'center' };
  const imagetdStyle: any = {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  };

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const initialDataState: State = {
    take: 50,
    skip: 0,
    group: [],
  };

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [csvData, setCSVData] = React.useState<NBCItemCSVRecord[]>([]);
  const SELECT_PROFILE = GetString('SELECT_PROFILE');

  const status = GetString('STATUS');

  const YES = GetString('YES');

  const NO = GetString('NO');

  const IMPORT_CSV_DATA = GetString('IMPORT_CSV_DATA');

  const CONFIRM_DELETE_MESSAGE = GetString('CONFIRM_DETETE_MESSAGE');

  const IMPORT_COMPLETED = GetString('IMPORT_COMPLETED');

  const IMPORT_FAILED = GetString('IMPORT_FAILED');

  const IMPORTING_BATCH = GetString('IMPORTING_BATCH');

  const COMPLETE = GetString('COMPLETE');

  const OF = GetString('OF');

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const nbcItems: NBCItem[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.nbcItems,
  );

  const fetchingNBCItems: boolean = useSelector(
    (state: ApplicationState) => state.nbcReducer.fetchingNbcItems,
  );

  const profileNames: NBCProfile[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.profileNames,
  );

  const selectedProfile: string | undefined = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedProfile,
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.nbcReducer.fetchError,
  );

  const imageList: string[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.nbcImages,
  );

  const notificationMessage: string = useSelector(
    (state: ApplicationState) => state.nbcReducer.messageNotification,
  );

  const applySnapshotResponse: ResultResponse = useSelector(
    (state: ApplicationState) => state.nbcReducer.response,
  );

  const itemsGroups: NBCItemGroups[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.groups,
  );

  const profileID: number | undefined = useSelector(
    (state: ApplicationState) =>
      state.nbcReducer.profileNames.find(
        item => item.ProfileUuid === selectedProfile,
      )?.ProfileId,
  );

  const [error, displayError] = React.useState(false);

  const [displayNotification, setDisplayNotification] = React.useState(false);

  const [snapshotNotification, setSnapshotNotification] = React.useState(false);

  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const [applySnapshotModalVisible, setApplySnapshotModalVisible] =
    React.useState<boolean>(false);

  const [disableButton, setDisableButton] = React.useState<boolean>(true);

  const [showErrorUploadImage, setShowErrorUploadImage] =
    React.useState<boolean>(false);

  const [isImporting, setIsImporting] = React.useState(false);

  const [importProgress, setImportProgress] = React.useState(0);

  const [importStatus, setImportStatus] = React.useState('');

  const [importedCount, setImportedCount] = React.useState(0);

  const [totalImages, setTotalImages] = React.useState(0);

  const processWithGroups = (data: any, dataState: State): DataResult => {
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(nbcItems, dataState),
  );

  const [commandSend, displayCommandSend] = React.useState(false);

  const [confirmVisible, setConfirmVisible] = React.useState<boolean>(false);

  const newData = setExpandedState({
    data: resultState.data,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    getProfileNameList(dispatch);
    getAllNBCImages(dispatch);
  }, []);

  useEffect(() => {
    setResultState(processWithGroups(nbcItems, dataState));
  }, [nbcItems]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(nbcItems, event.dataState);
    setResultState(newDataState);
    setDataState(event.dataState);
  };

  const inputRef = React.useRef<HTMLInputElement>(null);

  const importImagesInBatches = async (imageListInfo: ImageInfo[]) => {
    setIsImporting(true);
    setTotalImages(imageListInfo.length);

    const totalBatches = Math.ceil(
      imageListInfo.length / NBC_IMAGE_IMPORT_BATCH_SIZE,
    );

    try {
      let uploaded = 0;

      for (
        let i = 0;
        i < imageListInfo.length;
        i += NBC_IMAGE_IMPORT_BATCH_SIZE
      ) {
        const batch = imageListInfo.slice(i, i + NBC_IMAGE_IMPORT_BATCH_SIZE);

        const batchNumber = Math.floor(i / NBC_IMAGE_IMPORT_BATCH_SIZE) + 1;

        setImportStatus(
          `${IMPORTING_BATCH} ${batchNumber} ${OF} ${totalBatches}`,
        );

        await importImages(dispatch, batch);

        uploaded += batch.length;

        setImportedCount(uploaded);

        setImportProgress(Math.round((uploaded / imageListInfo.length) * 100));
      }

      setImportStatus(IMPORT_COMPLETED);
    } catch (error) {
      console.error(error);

      setImportStatus(IMPORT_FAILED);
    } finally {
      setTimeout(() => {
        setIsImporting(false);
        setImportProgress(0);
        setImportedCount(0);
      }, 1000);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const notValidImages = Array.from(files).filter(
      file => file.type !== 'image/png',
    );
    if (notValidImages.length > 0) {
      setShowErrorUploadImage(true);
      return;
    }

    const readFiles: Promise<ImageInfo>[] = Array.from(files).map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64String = result.replace(
            /^data:image\/[a-z]+;base64,/,
            '',
          );
          const imageInfo: ImageInfo = {
            ImageName: file.name,
            base64String: base64String,
          };
          resolve(imageInfo);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readFiles).then(async imageListInfo => {
      await importImagesInBatches(imageListInfo);
    });
  };

  const imageFileName = imageList.map(image =>
    image.split('?')[0].split('/').pop(),
  );

  const ImageCell = (props: GridCellProps) => {
    const [showImageDialog, setShowImageDialog] =
      React.useState<boolean>(false);

    const [selectedImage, setSelectedImage] = React.useState<string>(
      props.dataItem.ImagePath,
    );

    let isImageFind;

    if (selectedImage) {
      isImageFind = imageFileName.some(e => {
        const filename = selectedImage.split('?')[0].split('/').pop();
        return e === filename;
      });
    }

    const imageSelected = (image: string) => {
      setSelectedImage(image);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            ImagePath: image,
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
      setShowImageDialog(false);
    };

    return (
      <td style={imagetdStyle}>
        {isImageFind ? (
          <img
            src={selectedImage}
            className={classes.imageCell}
            alt="Uploaded"
          />
        ) : (
          <Input value="N/A" className={classes.imageCell} disabled={true} />
        )}
        {showImageDialog && (
          <ImageDialog
            handleCloseDialog={() => setShowImageDialog(false)}
            imageList={imageList}
            setSelectedImage={imageSelected}
          />
        )}
        <Button onClick={() => setShowImageDialog(true)}>...</Button>
      </td>
    );
  };

  const updatedItemKeys: any[] | undefined = [];

  let nbcItemData: NBCItem[] = nbcItems;

  const isDefaultChecked = (props: any) => {
    return !!props;
  };

  const isItemIDExist = (key: number) => {
    if (!updatedItemKeys.includes(key)) updatedItemKeys.push(key);
  };

  const GroupsCell = (props: GridCellProps) => {
    const GroupsOnChange = (e: DropDownListChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Group: e.value,
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <DynamicDropDownList
          data={['', ...itemsGroups.map(item => item.GroupName)]}
          defaultValue={props.dataItem.Group}
          onChange={GroupsOnChange}
          className={classes.fontSize}
          width={120}
        />
      </td>
    );
  };

  const QuantityRequiredCell = (props: GridCellProps) => {
    const QuantityRequiredOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            QuantityRequired: e.value,
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.QuantityRequired)}
          onChange={QuantityRequiredOnchange}
        />
      </td>
    );
  };

  const WeightRequiredCell = (props: GridCellProps) => {
    const WeightRequiredOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            WeightRequired: e.value,
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.WeightRequired)}
          onChange={WeightRequiredOnchange}
        />
      </td>
    );
  };

  const SpecialSecurityCell = (props: GridCellProps) => {
    const SpecialSecurityOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            SpecialSecurity: e.value,
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.SpecialSecurity)}
          onChange={SpecialSecurityOnchange}
        />
      </td>
    );
  };

  const EnabledCell = (props: GridCellProps) => {
    const EnabledOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return { ...item, IsEnabled: e.value };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.IsEnabled)}
          onChange={EnabledOnchange}
        />
      </td>
    );
  };

  const PurchaseByDeptCell = (props: GridCellProps) => {
    const PurchaseByDeptOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            PurchaseByDepartment: e.value,
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.PurchaseByDepartment)}
          onChange={PurchaseByDeptOnchange}
        />
      </td>
    );
  };

  const VisibleCell = (props: GridCellProps) => {
    const VisibleOnchange = (e: CheckboxChangeEvent) => {
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return { ...item, IsVisible: e.value };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Checkbox
          defaultChecked={isDefaultChecked(props.dataItem.IsVisible)}
          onChange={VisibleOnchange}
        />
      </td>
    );
  };

  const HitsCell = (props: GridCellProps) => {
    const [hits, setHits] = React.useState<any>(props.dataItem.Hits ?? '');
    const HitsOnchange = (e: InputChangeEvent) => {
      setHits(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Hits: e.target.value == '' ? 0 : parseInt(e.value),
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={hits === 0 ? '' : hits}
          onChange={HitsOnchange}
          type="number"
          className={classes.fontSize}
        />
      </td>
    );
  };

  const DepartmentCell = (props: GridCellProps) => {
    const [department, setDepartment] = React.useState<any>(
      props.dataItem.Department ?? '',
    );
    const departmentOnchange = (e: InputChangeEvent) => {
      setDepartment(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Department: e.value.toString(),
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={department}
          onChange={departmentOnchange}
          className={classes.fontSize}
        />
      </td>
    );
  };

  const PriceCell = (props: GridCellProps) => {
    const [price, setPrice] = React.useState<number | null>(
      props.dataItem.Price === null ? null : props.dataItem.Price / 100,
    );

    const PriceOnchange = (e: NumericTextBoxChangeEvent) => {
      const newValue = e.value ?? null;
      setPrice(newValue);

      if (newValue !== null) {
        const centsValue = Math.round(newValue * 100);

        const updateNBCItem = nbcItemData.map(item => {
          if (item.Key === props.dataItem.Key) {
            return {
              ...item,
              Price: centsValue.toString(),
            };
          }
          return item;
        });
        nbcItemData = [...updateNBCItem];
        isItemIDExist(props.dataItem.Key);
      }
    };
    return (
      <td style={tdStyle}>
        <NumericTextBox
          value={price}
          onChange={PriceOnchange}
          format="n2"
          className={classes.fontSize}
        />
      </td>
    );
  };

  const PLUCell = (props: GridCellProps) => {
    const [plu, setPLU] = React.useState<any>(props.dataItem.PLU ?? '');
    const pluOnchange = (e: InputChangeEvent) => {
      setPLU(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            PLU: e.value.toString(),
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={plu}
          onChange={pluOnchange}
          className={classes.fontSize}
        />
      </td>
    );
  };

  const CodeCell = (props: GridCellProps) => {
    const [code, setCode] = React.useState<any>(props.dataItem.Code ?? '');
    const CodeOnchange = (e: InputChangeEvent) => {
      setCode(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Code: e.value.toString(),
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={code}
          onChange={CodeOnchange}
          className={classes.fontSize}
        />
      </td>
    );
  };

  const EnglishCell = (props: GridCellProps) => {
    const [englishText, setEnglishText] = React.useState<any>('');

    React.useEffect(() => {
      if (
        props.dataItem &&
        props.dataItem.Text &&
        props.dataItem.Text.English
      ) {
        setEnglishText(props.dataItem.Text.English);
      }
    }, [props.dataItem]);

    const EnglishTextOnchange = (e: InputChangeEvent) => {
      setEnglishText(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Text: {
              English: e.target.value?.toString(),
              French: item.Text?.French,
              Spanish: item.Text?.Spanish,
            },
          };
        }
        return item;
      });

      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={englishText}
          onChange={EnglishTextOnchange}
          className={classes.fontSize}
        />
      </td>
    );
  };

  const SpanishCell = (props: GridCellProps) => {
    const [spanishText, setSpanishText] = React.useState<any>(''); // Provide default value for spanishText

    React.useEffect(() => {
      if (
        props.dataItem &&
        props.dataItem.Text &&
        props.dataItem.Text.Spanish
      ) {
        setSpanishText(props.dataItem.Text.Spanish);
      }
    }, [props.dataItem]);

    const SpanishTextOnchange = (e: InputChangeEvent) => {
      setSpanishText(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Text: {
              English: item.Text?.English,
              French: item.Text?.French,
              Spanish: e.target.value?.toString(),
            },
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={spanishText}
          onChange={SpanishTextOnchange}
          className={classes.fontSize}
        />
      </td>
    );
  };

  const FrenchCell = (props: GridCellProps) => {
    const [frenchText, setFrenchText] = React.useState<any>('');

    React.useEffect(() => {
      if (props.dataItem && props.dataItem.Text && props.dataItem.Text.French) {
        setFrenchText(props.dataItem.Text.French);
      }
    }, [props.dataItem]);

    const FrenchTextOnchange = (e: InputChangeEvent) => {
      setFrenchText(e.target.value);
      let updateNBCItem = nbcItemData.map(item => {
        if (item.Key === props.dataItem.Key) {
          return {
            ...item,
            Text: {
              English: item.Text?.English,
              French: e.target.value?.toString(),
              Spanish: item.Text?.Spanish,
            },
          };
        }
        return item;
      });
      nbcItemData = [...updateNBCItem];
      isItemIDExist(props.dataItem.Key);
    };
    return (
      <td style={tdStyle}>
        <Input
          value={frenchText}
          className={classes.fontSize}
          onChange={FrenchTextOnchange}
        />
      </td>
    );
  };

  const DateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.LastUpdated &&
      dataItem.LastUpdated != '0001-01-01T00:00:00'
    ) {
      return (
        <td>
          <p className={classes.fontSize}>
            {getLocalDateTime(dataItem.LastUpdated)}
          </p>
        </td>
      );
    }
    return <td></td>;
  };

  const [itemsToDelete, setItemsToDelete] = React.useState<string[]>([]);

  const DeleteNBCItemsCell = React.memo(({ dataItem }: GridCellProps) => {
    const isChecked = itemsToDelete.includes(dataItem.Key);

    const MarkDelete = React.useCallback(
      (e: CheckboxChangeEvent) => {
        setItemsToDelete(prevItems => {
          const updatedItems = e.value
            ? [...prevItems, dataItem.Key]
            : prevItems.filter(key => key !== dataItem.Key);

          return updatedItems.length > 0 ? updatedItems : [];
        });
      },
      [dataItem.Key],
    );

    return (
      <td style={tdStyle}>
        <Checkbox checked={isChecked} onChange={MarkDelete} />
      </td>
    );
  });

  const getCSVFileName = () => {
    return `NBC_items_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    let result: NBCItemCSVRecord[] = [];

    if (Array.isArray(nbcItems)) {
      nbcItems.forEach(item => {
        let data: NBCItemCSVRecord = {
          Code: item.Code,
          ImagePath: item.ImagePath,
          English: item.Text?.English,
          French: item.Text?.French,
          Spanish: item.Text?.Spanish,
          QuantityRequired: item.QuantityRequired,
          WeightRequired: item.WeightRequired,
          SpecialSecurity: item.SpecialSecurity,
          IsEnabled: item.IsEnabled,
          IsVisible: item.IsVisible,
          Hits: item.Hits,
          PurchaseByDepartment: item.PurchaseByDepartment,
          Department: item.Department,
          Price: item.Price,
          PLU: item.PLU,
          Group: item.Group,
        };
        result.push(data);
      });
    }
    setCSVData(result);
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  const [addItemsModalVisible, setAddItemsModalVisible] =
    React.useState<boolean>(false);

  const [addNewProfileModalVisible, setAddNewProfileModalVisible] =
    React.useState<boolean>(false);

  const [isSnapshotButtonDisabled, setIsSnapshotButtonDisabled] =
    React.useState<boolean>(false);

  const saveOnClick = () => {
    setResultState(processWithGroups(nbcItemData, dataState));

    let itemsToUpdate = nbcItemData
      .map(item => item)
      .filter(item => updatedItemKeys.includes(item.Key));

    updateNBCItems(
      dispatch,
      itemsToUpdate,
      profileNames.find(item => item.ProfileUuid === selectedProfile)
        ?.ProfileId,
    );
  };

  const siteNumberSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.notificationSendToSiteNumberSuccessMessage,
  );

  const nodeGroupsSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.notificationSendToNodeGroupsSuccessMessage,
  );
  const lanesSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.notificationSendToLanesSuccessMessage,
  );

  useEffect(() => {
    let timeoutId: any;
    if (commandSend) {
      timeoutId = setTimeout(() => {
        displayCommandSend(false);
        setShowNotification(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandSend]);

  const checkSuccessMessageValue =
    siteNumberSuccessMessage !== '' ||
    lanesSuccessMessage !== '' ||
    nodeGroupsSuccessMessage !== '';

  const [showNotification, setShowNotification] = React.useState(false);

  useEffect(() => {
    if (showNotification && checkSuccessMessageValue) displayCommandSend(true);
  }, [
    showNotification,
    siteNumberSuccessMessage,
    nodeGroupsSuccessMessage,
    lanesSuccessMessage,
  ]);

  const [showImportStatus, setShowImportStatus] =
    React.useState<boolean>(false);

  useEffect(() => {
    let timeoutId: any;
    if (showImportStatus) {
      timeoutId = setTimeout(() => {
        setShowImportStatus(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showImportStatus]);

  const [showFileDialog, setShowFileDialog] = React.useState<boolean>(false);

  const [visible, setVisible] = React.useState<boolean>(false);
  const [imageDialogVisible, setImageDialogVisible] =
    React.useState<boolean>(false);

  const importCSVActionData = [
    { label: GetString('ADD'), value: GetString('ADD') },
    { label: GetString('OVERWRITE'), value: GetString('OVERWRITE') },
  ];

  const [selectedAction, setSelectedAction] = React.useState(
    importCSVActionData[0].value,
  );

  const importCSVActionChange = (e: RadioGroupChangeEvent) => {
    setSelectedAction(e.value);
  };

  const closeDialog = () => {
    setVisible(false);
  };

  const toggleDialog = () => {
    closeDialog();
    setShowFileDialog(true);
  };

  const importButtonCLick = () => {
    setVisible(true);
  };

  const convertStringToBool = (value: string | undefined) => {
    return value?.toLowerCase() === 'true';
  };

  const handleFileUpload = (
    data: CsvRow[],
    fileName: string,
    detectedSchema: string,
  ) => {
    let importNBCItem: NBCItem[] = [];

    if (detectedSchema === 'ENHNBC') {
      console.log('Importing ENHNBC NBC items CSV');
      importNBCItem = data.map(item => {
        return {
          Code: item['Code'],
          ImagePath: item['Image'],
          IsEnabled: convertStringToBool(item['Enabled']),
          IsVisible: convertStringToBool(item['Visible']),
          Text: {
            English: item['English'],
            French: item['French'],
            Spanish: item['Spanish'],
          },
          Group: item['Group'],
          QuantityRequired: convertStringToBool(item['Quantity Required']),
          WeightRequired: convertStringToBool(item['Weight Required']),
          SpecialSecurity: convertStringToBool(item['Special Security']),
          Hits: item['Hits'] ? Number(item['Hits']) : 0,
          PurchaseByDepartment: convertStringToBool(
            item['Purchase By Department'],
          ),
          Department: item['Department'],
          Price: item['Price'],
          PLU: item['PLU'],
        };
      });
    } else if (detectedSchema === 'Enterprise') {
      console.log('Importing Enterprise NBC items CSV');
      importNBCItem = data.map(item => {
        return {
          Code: item['Code'],
          ImagePath: item['ImagePath'],
          IsEnabled: convertStringToBool(item['IsEnabled']),
          IsVisible: convertStringToBool(item['IsVisible']),
          Text: {
            English: item['English'],
            French: item['French'],
            Spanish: item['Spanish'],
          },
          Group: item['Group'],
          QuantityRequired: convertStringToBool(item['QuantityRequired']),
          WeightRequired: convertStringToBool(item['WeightRequired']),
          SpecialSecurity: convertStringToBool(item['SpecialSecurity']),
          Hits: item['Hits'] ? Number(item['Hits']) : undefined,
          PurchaseByDepartment: convertStringToBool(
            item['PurchaseByDepartment'],
          ),
          Department: item['Department'],
          Price: item['Price'],
          PLU: item['PLU'],
        };
      });
    } else {
      console.error('Skipping NBC import (unexpected CSV type)');
      setShowImportStatus(true);
      return;
    }

    const newNBCItems: NBCItem[] = nbcItems.map(item => {
      return {
        Code: item.Code,
        ImagePath: item.ImagePath,
        Text: {
          English: item.Text?.English,
          French: item.Text?.French,
          Spanish: item.Text?.Spanish,
        },
        QuantityRequired: item.QuantityRequired,
        WeightRequired: item.WeightRequired,
        SpecialSecurity: item.SpecialSecurity,
        IsEnabled: item.IsEnabled,
        IsVisible: item.IsVisible,
        Hits: item.Hits ? item.Hits : undefined,
        PurchaseByDepartment: item.PurchaseByDepartment,
        Price: item.Price,
        Group: item.Group,
      };
    });

    let newItem: NBCItem[] = [];
    const itemCodeList: any[] = nbcItems.map(item => item.Code);
    const newCodeToAdd: any[] = [];

    if (selectedAction === importCSVActionData[0].value) {
      if (newNBCItems.length === 0) {
        addNewNBCItem(
          dispatch,
          importNBCItem.map(item => {
            return {
              ...item,
            };
          }),
          profileNames.find(item => item.ProfileUuid === selectedProfile)
            ?.ProfileId,
        );

        return;
      }

      for (let i = 0; i < newNBCItems.length; i++) {
        for (let j = 0; j < importNBCItem.length; j++) {
          if (
            !itemCodeList.includes(importNBCItem[j].Code) &&
            !newCodeToAdd.includes(importNBCItem[j].Code)
          ) {
            newCodeToAdd.push(importNBCItem[j].Code);
            newItem.push({
              ...importNBCItem[j],
            });
          }
        }
      }
    } else if (selectedAction === importCSVActionData[1].value) {
      if (!_.isEqual(newNBCItems, importNBCItem)) {
        overwriteNBCItem(
          dispatch,
          importNBCItem.map(item => {
            return {
              ...item,
            };
          }),
          profileNames.find(item => item.ProfileUuid === selectedProfile)
            ?.ProfileId,
        );
      }
    }

    if (
      newItem.length === 0 &&
      selectedAction === importCSVActionData[0].value
    ) {
      dispatch({
        type: NBCActionTypes.SET_NOTIFICATION_MESSAGE,
        messageNotification: 'NOTIFICATION_MESSAGE',
      });
    } else if (newItem.length > 0) {
      addNewNBCItem(
        dispatch,
        newItem,
        profileNames.find(item => item.ProfileUuid === selectedProfile)
          ?.ProfileId,
      );

      newItem = [];
    }
  };

  const importButtonClick = (bool: boolean) => {
    setShowFileDialog(bool);
  };

  const profileOnChange = (event: DropDownListChangeEvent) => {
    saveSelectedProfile(
      dispatch,
      profileNames.find(a => a.ProfileName === event.target.value)?.ProfileUuid,
    );

    const profileId = profileNames.find(
      item =>
        item.ProfileUuid ===
        profileNames.find(a => a.ProfileName === event.target.value)
          ?.ProfileUuid,
    )?.ProfileId;

    if (profileId !== undefined) {
      fetchNBCItemsByID(dispatch, profileId);

      getItemsGroups(dispatch, profileId);
    }
  };

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  useEffect(() => {
    if (notificationMessage != '') {
      setDisplayNotification(true);
      setTimeout(() => {
        setDisplayNotification(false);
        resetNotificationMessage(dispatch);
      }, 3000);
    }
  }, [notificationMessage]);

  useEffect(() => {
    if (applySnapshotResponse.Message != '') {
      setSnapshotNotification(true);
      setTimeout(() => {
        setSnapshotNotification(false);
        resetNotificationMessage(dispatch);
      }, 3000);
    }
  }, [applySnapshotResponse]);

  useEffect(() => {
    setDisableButton(
      profileNames.find(item => item.ProfileUuid === selectedProfile)
        ? false
        : true,
    );

    setIsSnapshotButtonDisabled(
      profileNames.find(item => item.ProfileUuid === selectedProfile)
        ?.Divisions === undefined ||
        profileNames.find(item => item.ProfileUuid === selectedProfile)
          ?.Divisions === null,
    );
  }, [selectedProfile, profileNames]);

  const clearFetchError = () => {
    displayError(false);
    resetFetchError(dispatch);
  };

  const loadItemsClicked = () => {
    const profileId = profileNames.find(
      item => item.ProfileUuid === selectedProfile,
    )?.ProfileId;
    if (profileId !== undefined) {
      fetchNBCItemsByID(dispatch, profileId);
    }
  };

  useEffect(() => {
    if (showErrorUploadImage) {
      setTimeout(() => {
        setShowErrorUploadImage(false);
      }, 3000);
    }
  }, [showErrorUploadImage]);

  const deleteConfirmationClicked = () => {
    deleteNBCItems(
      dispatch,
      itemsToDelete,
      profileNames.find(item => item.ProfileUuid === selectedProfile)
        ?.ProfileId,
    );

    setConfirmVisible(false);
    setItemsToDelete([]);
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  const [showManageGroupDialog, setShowManageGroupDialog] =
    React.useState(false);

  useEffect(() => {
    const profileId = profileNames.find(
      item => item.ProfileUuid === selectedProfile,
    )?.ProfileId;

    if (profileId !== undefined) {
      fetchNBCItemsByID(dispatch, profileId);
    }
  }, [profileNames, itemsGroups]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <CSVLink
            data={csvData}
            ref={csvLinkRef}
            filename={getCSVFileName() + '.csv'}
          />
          {fetchingNBCItems && LoadingPanel}
          <Grid
            style={{ height: '90vh' }}
            resizable={true}
            sortable={true}
            filterable={false}
            pageable={{
              buttonCount: 4,
              pageSizes: [20, 50, 100],
            }}
            data={resultState}
            {...dataState}
            groupable={true}
            reorderable={true}
            fixedScroll={false}
            onDataStateChange={dataStateChange}
            cells={{
              headerCell: HeaderCustomCell,
            }}
            total={nbcItems.length}
          >
            <GridToolbar>
              <div className={classes.toolbarButtonContainer}>
                <div className={classes.toolbarButtonGrid}>
                  <DynamicDropDownList
                    className={classes.toolbarButton}
                    data={profileNames.map(a => a.ProfileName)}
                    defaultValue={SELECT_PROFILE}
                    onChange={profileOnChange}
                    value={
                      profileNames.find(
                        item => selectedProfile === item.ProfileUuid,
                      )?.ProfileName
                    }
                    width={150}
                  />
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => {
                      setAddNewProfileModalVisible(true);
                    }}
                  >
                    {GetString('MANAGE_PROFILES')}
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => {
                      setShowManageGroupDialog(true);
                    }}
                    disabled={disableButton}
                  >
                    {GetString('MANAGE_GROUPS')}
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => {
                      setAddItemsModalVisible(true);
                    }}
                    disabled={disableButton}
                  >
                    {GetString('ADD_ITEM_TEXT')}
                  </Button>

                  <Button
                    type="submit"
                    disabled={isImporting}
                    className={classes.toolbarButton}
                    onClick={() => inputRef.current?.click()}
                  >
                    {GetString('IMPORT_IMAGE')}
                  </Button>
                  <input
                    type="file"
                    accept=".png"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    ref={inputRef}
                    multiple
                  />
                </div>
                <Divider />
                <div className={classes.toolbarButtonGrid}>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={loadItemsClicked}
                    disabled={disableButton}
                  >
                    {GetString('LOAD_ITEMS_BUTTON')}
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={saveOnClick}
                    disabled={disableButton}
                  >
                    {GetString('SAVE_BUTTON')}
                    <SaveIcon className={classes.icon} />
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={processExportCSV}
                    disabled={disableButton}
                  >
                    {GetString('EXPORT_TO_CSV')}
                    <FileDownloadIcon className={classes.icon} />
                  </Button>
                  <CustomCSVReader
                    openFileDialog={showFileDialog}
                    handleFileUpload={handleFileUpload}
                    importButtonClick={importButtonClick}
                    schemas={NBC_ITEM_CSV_SCHEMAS}
                  />
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={importButtonCLick}
                    disabled={disableButton}
                  >
                    {GetString('IMPORT_CSV')}
                    <UploadIcon className={classes.icon} />
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => {
                      setApplySnapshotModalVisible(true);
                    }}
                    disabled={isSnapshotButtonDisabled || nbcItems.length === 0}
                  >
                    {GetString('APPLY_SNAPSHOT')}
                    <ScreenshotMonitorIcon className={classes.icon} />
                  </Button>
                  <Button
                    type="submit"
                    className={classes.toolbarButton}
                    onClick={() => {
                      setConfirmVisible(true);
                    }}
                    disabled={itemsToDelete.length === 0}
                  >
                    {GetString('DELETE_ITEM_BUTTON')}
                  </Button>
                </div>
              </div>
            </GridToolbar>
            {isImporting && (
              <GridToolbar>
                <div className={classes.imageImportProgressToolbar}>
                  <span>{importStatus}</span>
                  <div style={{ width: 250 }}>
                    <ProgressBar value={importProgress} min={0} max={100} />
                  </div>
                  <span>
                    {importedCount} / {totalImages}
                  </span>
                  <span>
                    {importProgress}% {COMPLETE}
                  </span>
                </div>
              </GridToolbar>
            )}
            <GridColumn width="50px" cells={{ data: DeleteNBCItemsCell }} />
            <GridColumn
              {...columnProps('Code')}
              title={GetString('COLUMN_TITLE_CODE')}
              width="100px"
              cells={{ data: CodeCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              {...columnProps('ImagePath')}
              title={GetString('COLUMN_TITLE_IMAGE')}
              width="150px"
              filterable={false}
              cells={{ data: ImageCell }}
            />
            <GridColumn
              {...columnProps('Text.English')}
              title={GetString('COLUMN_TITLE_ENGLISH')}
              width="150px"
              cells={{ data: EnglishCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              {...columnProps('Text.French')}
              title={GetString('COLUMN_TITLE_FRENCH')}
              width="150px"
              cells={{ data: FrenchCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              {...columnProps('Text.Spanish')}
              title={GetString('COLUMN_TITLE_SPANISH')}
              width="150px"
              cells={{ data: SpanishCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              field="QuantityRequired"
              title={GetString('COLUMN_TITLE_QUANTITY_REQUIRED')}
              width="140px"
              cells={{ data: QuantityRequiredCell }}
              className={classes.column}
            />
            <GridColumn
              field="WeightRequired"
              title={GetString('COLUMN_TITLE_WEIGHT_REQUIRED')}
              width="130px"
              cells={{ data: WeightRequiredCell }}
            />
            <GridColumn
              field="SpecialSecurity"
              title={GetString('COLUMN_TITLE_SPECIAL_SECURITY')}
              width="130px"
              cells={{ data: SpecialSecurityCell }}
            />
            <GridColumn
              field="IsEnabled"
              title={GetString('COLUMN_TITLE_ENABLED')}
              width="80px"
              cells={{ data: EnabledCell }}
            />
            <GridColumn
              field="IsVisible"
              title={GetString('COLUMN_TITLE_VISIBLE')}
              width="80px"
              cells={{ data: VisibleCell }}
            />
            <GridColumn
              {...columnProps('Hits')}
              title={GetString('COLUMN_TITLE_HITS')}
              width="100px"
              cells={{ data: HitsCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              {...columnProps('Group')}
              title={GetString('COLUMN_TITLE_GROUPS')}
              width="150px"
              cells={{ data: GroupsCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              field="PurchaseByDepartment"
              title={GetString('COLUMN_TITLE_PURCHASE_BY_DEPT')}
              width="150px"
              cells={{ data: PurchaseByDeptCell }}
            />
            <GridColumn
              {...columnProps('Department')}
              title={GetString('COLUMN_TITLE_DEPARTMENT')}
              width="130px"
              cells={{ data: DepartmentCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              {...columnProps('Price')}
              title={GetString('COLUMN_TITLE_PRICE')}
              width="100px"
              cells={{ data: PriceCell }}
              columnMenu={ColumnMenu}
            />

            <GridColumn
              {...columnProps('PLU')}
              title={GetString('COLUMN_TITLE_PLU')}
              width="100px"
              cells={{ data: PLUCell }}
              columnMenu={ColumnMenu}
            />
            <GridColumn
              field="LastUpdated"
              title={GetString('LAST_UPDATED')}
              width="180px"
              cells={{ data: DateTimeCell }}
            />
          </Grid>
          {visible && (
            <Dialog title={IMPORT_CSV_DATA} onClose={closeDialog}>
              <div style={{ marginBottom: '20px' }}>
                <StringResourceManager stringKey={'SELECT_NBC_IMPORT_METHOD'} />
              </div>
              <RadioGroup
                data={importCSVActionData}
                value={selectedAction}
                onChange={importCSVActionChange}
                layout="horizontal"
              />
              <DialogActionsBar>
                <Button type="button" onClick={closeDialog}>
                  <StringResourceManager stringKey={'CANCEL_BUTTON'} />
                </Button>
                <Button type="button" onClick={toggleDialog}>
                  <StringResourceManager stringKey={'IMPORT_BUTTON'} />
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}
          {addItemsModalVisible && (
            <AddNBCItemDialog
              handleCloseDialog={() => setAddItemsModalVisible(false)}
              profile={selectedProfile}
            />
          )}
          {applySnapshotModalVisible && (
            <ApplySnapshotDialog
              handleCloseDialog={() => setApplySnapshotModalVisible(false)}
              modalTitle={'APPLY_SNAPSHOT_MODAL_TITLE'}
              snapShotType="NBC Item"
            />
          )}
          {addNewProfileModalVisible && (
            <ManageProfileDialog
              handleCloseDialog={() => setAddNewProfileModalVisible(false)}
            />
          )}
          {imageDialogVisible && (
            <ImageDialog
              handleCloseDialog={() => setImageDialogVisible(false)}
              imageList={imageList}
            />
          )}
          {confirmVisible && (
            <Dialog
              style={{ zIndex: 10001 }}
              title="Confirmation"
              onClose={() => {
                setConfirmVisible(false);
              }}
            >
              <p>{CONFIRM_DELETE_MESSAGE}</p>
              <DialogActionsBar>
                <Button onClick={deleteConfirmationClicked}>{YES}</Button>
                <Button
                  onClick={() => {
                    setConfirmVisible(false);
                  }}
                >
                  {NO}
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}
          {showManageGroupDialog && (
            <ManageProfileGroupsDialog
              handleCloseDialog={() => setShowManageGroupDialog(false)}
            />
          )}
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {commandSend && (
                <Notification
                  className={classes.send}
                  type={{
                    style: checkSuccessMessageValue ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        checkSuccessMessageValue
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : 'COMMAND_SEND_FAILED'
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showImportStatus && (
                <Notification
                  className={classes.send}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={'FAILED_TO_IMPORT_INCORRECT_FORMAT'}
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {error && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => clearFetchError()}
                >
                  <StringResourceManager stringKey={fetchError.Message} />
                  {`(${status}: ${fetchError.StatusCode})`}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {displayNotification && (
                <Notification
                  className={classes.error}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => clearFetchError()}
                >
                  <StringResourceManager stringKey={notificationMessage} />
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {snapshotNotification && (
                <Notification
                  className={classes.error}
                  type={{
                    style: applySnapshotResponse.IsOk ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                  onClose={() => clearFetchError()}
                >
                  <StringResourceManager
                    stringKey={applySnapshotResponse.Message}
                  />
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showErrorUploadImage && (
                <Notification
                  className={classes.send}
                  type={{
                    style: 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {<StringResourceManager stringKey={'ERROR_IMPORT_IMAGE'} />}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default NBCItemsGrid;
